import cx from 'classnames';
import * as QRCode from 'qrcode';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'components/Button';
import { CopyButton } from 'components/CopyButton';
import { CountdownTimer } from 'components/CountdownTimer';
import { PaymentLayoutAction } from 'components/PaymentLayout';
import { CLARITY_MASK_PROPS } from 'constants/clarity.constants';
import { useToast } from 'context';
import { DeviceOS, EventName } from 'enums';
import { useOrderContext } from 'hooks';
import { clipboardUtils, eventUtils, saveImage, userDeviceUtils } from 'utils';

type Props = {
  actions: PaymentLayoutAction[];
  deeplinksSlot?: React.ReactNode;
};

const TEXT = {
  title: 'Оплата счёта',
  orderId: 'ID счёта:',
  exchangeRateNotice:
    'Важно! Сумма при оплате в банке может незначительно отличаться из-за курса обмена.',
  understood: 'Понятно',
  qrAlt: 'QR-код',
  saveQr: 'Сохранить QR',
  payHint: 'Для быстрой оплаты через приложение нажмите на кнопку ниже',
  fallbackHint:
    'Если при нажатии кнопки "Оплатить" вас не перенаправило в приложение банка, воспользуйтесь способом представленным ниже.',
  desktopInstruction:
    'Откройте приложение Сбербанк на телефоне, выберите оплату по QR-коду и отсканируйте этот код внутри приложения Сбербанк.',
  sberbankOnlyWarning:
    'Оплата только со Сбербанка. Оплата с других банков не принимается.',
  mobileStep1: '1. Нажмите "Сохранить QR".',
  mobileStep2:
    '2. Запустите приложение Сбербанк и откройте раздел работы с QR.',
  mobileStep3: '3. Выберите QR-код из галереи в приложении Сбербанка.',
  paid: 'Я оплатил',
  cancel: 'Отмена',
};

const formatAmount = (amount: number, currency: string) =>
  `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)} ${currency}`;

export const NspkCustomPaymentForm: React.FC<Props> = ({
  actions,
  deeplinksSlot,
}) => {
  const {
    order,
    orderId,
    requisites,
    requisitesPaymentLink,
    currencySymbol,
    refetchOrderStatus,
  } = useOrderContext();
  const { showCopiedToast, showCopyFailedToast } = useToast();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [noticeOpen, setNoticeOpen] = useState(true);

  const returnAction = actions[0];
  const paidAction = actions[1];
  const isIOS = useMemo(
    () => userDeviceUtils.getDeviceOS() === DeviceOS.IOS,
    [],
  );

  const amount = useMemo(
    () => formatAmount(order.amount, order.currency || currencySymbol),
    [currencySymbol, order.amount, order.currency],
  );

  const qrSource =
    requisites?.paymentStringVietnamSberQR ||
    requisites?.paymentString ||
    requisites?.rawQrCodeData ||
    requisites?.qrImageUrl ||
    requisitesPaymentLink ||
    requisites?.phone ||
    orderId;

  useEffect(() => {
    const languageSelectorContainer =
      document.getElementById('language-selector')?.parentElement;
    const previousDisplay = languageSelectorContainer?.style.display;

    if (languageSelectorContainer) {
      languageSelectorContainer.style.display = 'none';
    }

    return () => {
      if (languageSelectorContainer) {
        languageSelectorContainer.style.display = previousDisplay || '';
      }
    };
  }, []);

  useEffect(() => {
    if (!qrSource || requisites?.qrImageUrl) {
      return;
    }

    QRCode.toDataURL(qrSource, {
      margin: 1,
      width: 220,
    }).then(setQrDataUrl);
  }, [qrSource, requisites?.qrImageUrl]);

  const qrImageSrc = requisites?.qrImageUrl || qrDataUrl;

  const handleSaveQr = useCallback(() => {
    if (!qrImageSrc) {
      return;
    }
    eventUtils.trackEvent({
      event: EventName.SaveQr,
    });
    void saveImage(qrImageSrc, `qr-${orderId}.png`);
  }, [qrImageSrc, orderId]);

  const renderDeeplinksSlot = () => {
    if (!deeplinksSlot) {
      return null;
    }

    return (
      <div className="tw-mb-4">
        <p className="tw-mb-2 tw-text-center tw-text-sm tw-text-[#6b7280]">
          {TEXT.payHint}
        </p>
        {deeplinksSlot}
        <p className="tw-text-sm tw-text-[#5f6368]">{TEXT.fallbackHint}</p>
      </div>
    );
  };

  return (
    <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[1120px] tw-rounded-[18px] tw-bg-white tw-p-4 tw-text-[#111827] md:tw-p-6 md:tw-shadow-xl">
      {noticeOpen && (
        <div className="tw-absolute tw-inset-0 tw-z-[1] tw-rounded-[18px] tw-bg-black tw-opacity-70" />
      )}
      <div
        className={cx('tw-relative tw-z-10 tw-mb-8', {
          'tw-text-white': noticeOpen,
        })}
      >
        <div className="tw-grid tw-gap-5 md:tw-grid-cols-[1fr_auto] md:tw-items-start">
          <div>
            <h1 className="tw-mb-3 tw-text-2xl tw-font-bold tw-leading-tight">
              {TEXT.title}
            </h1>
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-[#6b7280]">
              <span className="tw-break-all">{`${TEXT.orderId} ${orderId}`}</span>
              <CopyButton
                inline
                onClick={() =>
                  clipboardUtils.copyToClipboardWithFeedback(
                    orderId,
                    showCopiedToast,
                    showCopyFailedToast,
                  )
                }
              />
            </div>
          </div>
          <div
            className={cx(
              'tw-text-4xl tw-font-bold tw-leading-none md:tw-text-5xl',
              noticeOpen ? 'tw-text-white' : 'tw-text-black',
            )}
          >
            {amount}
          </div>
        </div>
        {noticeOpen && (
          <div className="tw-absolute tw-left-0 tw-right-0 tw-top-full tw-mt-4 tw-rounded-b-xl tw-bg-layout-accent-alt tw-p-4 tw-text-white">
            <span className="tw-absolute tw-top-[-1.2rem] tw-left-8 tw-scale-x-150 tw-text-2xl tw-text-layout-accent-alt md:tw-left-auto md:tw-right-8">
              {'▲'}
            </span>
            <div className="tw-text-sm tw-font-medium">
              {TEXT.exchangeRateNotice}
            </div>
            <div className="tw-mt-4 tw-w-32">
              <Button
                heightClassName="tw-h-10"
                color="success"
                onClick={() => setNoticeOpen(false)}
              >
                {TEXT.understood}
              </Button>
            </div>
          </div>
        )}
      </div>

      {!isIOS && renderDeeplinksSlot()}

      <div className="tw-mb-4 tw-grid tw-gap-8 tw-rounded-[18px] tw-bg-[#f5f5f5] tw-p-5 md:tw-min-h-[360px] md:tw-grid-cols-[120px_minmax(0,1fr)_260px] md:tw-items-center md:tw-p-8">
        <div className="tw-flex tw-justify-center md:tw-justify-start">
          <CountdownTimer
            order={order}
            refetchOrderStatus={refetchOrderStatus}
          />
        </div>

        <div className="tw-min-w-0">
          <div className="tw-max-w-[640px] tw-text-lg tw-leading-[1.55] tw-text-[#5f6368] md:tw-text-2xl">
            <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
              <span>{TEXT.mobileStep1}</span>
              <span>{TEXT.mobileStep2}</span>
              <span>{TEXT.mobileStep3}</span>
            </div>
            <span className="tw-hidden md:tw-inline">
              {TEXT.desktopInstruction}
            </span>
            <div className="tw-mt-4 tw-rounded-xl tw-border-2 tw-border-red-500 tw-p-3 tw-text-center tw-text-base tw-font-bold tw-text-red-500 md:tw-text-xl">
              {TEXT.sberbankOnlyWarning}
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 md:tw-items-end">
          {qrImageSrc && (
            <Button
              type="button"
              onClick={handleSaveQr}
              color="tertiary"
              className="tw-w-[240px] !tw-h-11 !tw-rounded-[10px] tw-text-sm md:tw-hidden"
            >
              {TEXT.saveQr}
            </Button>
          )}
          {isIOS && renderDeeplinksSlot()}
          {qrImageSrc ? (
            <button
              type="button"
              onClick={handleSaveQr}
              title={TEXT.saveQr}
              aria-label={TEXT.saveQr}
              className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0"
            >
              <img
                className="tw-h-[240px] tw-w-[240px]"
                src={qrImageSrc}
                alt={TEXT.qrAlt}
                {...CLARITY_MASK_PROPS}
              />
            </button>
          ) : (
            <div className="tw-h-[240px] tw-w-[240px] tw-bg-[#f3f4f6]" />
          )}
        </div>
      </div>

      <div className="tw-mx-auto tw-grid tw-w-full tw-gap-4 md:tw-max-w-[720px] md:tw-grid-cols-2 md:tw-items-center">
        <Button
          type="button"
          onClick={returnAction?.onClick}
          color="error"
          className="!tw-h-11 !tw-rounded-[10px] tw-text-sm md:!tw-h-12"
        >
          {TEXT.cancel}
        </Button>
        <Button
          type="button"
          onClick={paidAction?.onClick}
          color="success"
          className="!tw-h-11 !tw-rounded-[10px] tw-text-sm md:!tw-h-12"
        >
          {TEXT.paid}
        </Button>
      </div>
    </div>
  );
};
